/**
 * KBT-176 — events sourced from the shared Google Calendar.
 *
 * Why this goes through the backend instead of fetching the .ics feed
 * directly from the browser (as the ticket's "try client-side first"
 * guidance suggested we attempt):
 *
 *  1. CORS: calendar.google.com's ICS export does not return an
 *     Access-Control-Allow-Origin header, so a same-origin browser
 *     `fetch()` of it is blocked regardless of the calendar's sharing
 *     settings. This isn't a credentials issue, it's just how that
 *     endpoint is served.
 *  2. Credential exposure: a calendar that is "public" in the sense KBT
 *     needs (readable by anyone with the link, not necessarily
 *     discoverable/subscribable by search) gets a *secret* ICS address
 *     from Google (".../private-XXXXXXXX/basic.ics"). That address acts
 *     like a bearer credential — anyone who has it can read the
 *     calendar. Create-react-app bakes every REACT_APP_* env var
 *     verbatim into the publicly served JS bundle at build time, so
 *     putting that secret URL in a client env var would leak it to any
 *     site visitor who opens devtools.
 *
 * So the calendar URL lives ONLY in the backend's env
 * (KBT_EVENTS_CALENDAR_URL, kbt-backend/.env) and kbt-backend exposes a
 * public GET /events/calendar endpoint that fetches the feed server-side
 * and hands back the raw ICS text (or an empty/"not configured" result
 * if the client hasn't set the env var yet). This module just calls that
 * endpoint and parses the result — see src/utils/ical.js for the parsing
 * and recurrence-expansion logic.
 */
import Api from '../../utils/axios';
import { getUpcomingEvents } from '../../utils/ical';

export const fetchCalendarEvents = async () => {
  try {
    const response = await Api.get('/events/calendar');
    const { status, ics } = response?.data || {};

    if (status && status !== 200) return [];
    if (!ics) return [];

    return getUpcomingEvents(ics);
  } catch (error) {
    // A calendar that isn't configured yet, or a transient failure
    // reaching Google, should never break the Events page — just show no
    // calendar events instead of erroring the whole page out.
    return [];
  }
};
